import { useContext, useEffect, useMemo, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useUniqueId } from '@wix/da-hooks/pkg/useUniqueId';
import { SubmitFormContext } from '../context';
import { InitAdapter, SubmitAdapter, FieldValidator } from '../types';
import { getDefaultValues } from '../redux/selectors';

interface FieldOptions {
  initAdapter?: InitAdapter;
  submitAdapter?: SubmitAdapter;
  submitIfCleanOnEdit?: boolean | string[];
  validator?: FieldValidator;
  /* if field derrives its state from other fields */
  derivedFromFields?: string[];
}

export function useSubmitField(name: string, options: FieldOptions = {}) {
  const id = useUniqueId();

  const submitFormContext = useContext(SubmitFormContext);
  const formContext = useFormContext();
  const defaultValues = useSelector(getDefaultValues);

  // If SubmitFormContext exists we'll try to hook adapters into the submit form.
  // Otherwise it means it's a custom form and we'll basically just act like a regular useController-type field.
  const {
    initAdapter,
    submitAdapter,
    submitIfCleanOnEdit,
    validator,
    derivedFromFields = [],
  } = options;
  const {
    initResponse,
    isEdit,
    isDraft,
    params,
    registerValidator,
    registerSubmitAdapter,
    unregisterValidator,
    unregisterSubmitAdapter,
    scrollToField,
  } = submitFormContext || {};

  const defaultValue = useMemo(
    () =>
      initAdapter && initResponse
        ? initAdapter(
            initResponse,
            { ...params!, isEdit, isDraft },
            isEdit || isDraft ? undefined : defaultValues
          )
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      initAdapter,
      initResponse,
      initResponse?.galleries,
      initResponse?.pcp,
      initResponse?.pcp?.assets?.length,
      params,
      defaultValues,
      isEdit,
      isDraft,
    ]
  );

  const { control, errors } = formContext;
  const fieldRef = useRef<HTMLDivElement | null>(undefined);
  const { field } = useController({
    name,
    control,
    defaultValue,
    onFocus: () => {
      if (fieldRef.current && scrollToField) {
        scrollToField(fieldRef.current);
      }
    },
  });
  const error = errors[name];

  useEffect(() => {
    if (registerValidator && validator) {
      registerValidator(name, validator);
    }
    if (registerSubmitAdapter && submitAdapter) {
      registerSubmitAdapter(name, (data, formState) => {
        if (!formState) {
          return {};
        }
        const { dirtyFields } = formState;
        const isFieldDirty =
          dirtyFields.hasOwnProperty(name) ||
          derivedFromFields.some(derrivedFieldName =>
            dirtyFields.hasOwnProperty(derrivedFieldName)
          );
        // This field is submittable if the form is a new deviation,
        // OR the field is dirty, OR it should be submitted even if clean
        const shouldSubmit =
          !isEdit ||
          isFieldDirty ||
          submitIfCleanOnEdit === true ||
          (submitIfCleanOnEdit || []).includes(name);
        return shouldSubmit ? submitAdapter(data, formState) : {};
      });
    }

    return () => {
      if (unregisterValidator && validator) {
        unregisterValidator(name);
      }
      if (unregisterSubmitAdapter && submitAdapter) {
        unregisterSubmitAdapter(name);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    id,
    error,
    field,
    ref: fieldRef,
  };
}
